import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid,
  Chip,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities, clearError } from '../store/slices/entitySlice';
import { showSuccess, showError, showInfo } from '../store/slices/toastSlice';
import { Entity, EntityStatus } from '../types';

interface CreateEntityForm {
  name: string;
  description: string;
}

const Entities: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { entities, isLoading, error } = useAppSelector((state) => state.entities);

  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateEntityForm>({
    name: '',
    description: '',
  });

  useEffect(() => {
    dispatch(fetchEntities());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showError({
        title: 'Error',
        message: error,
        duration: 5000,
      }));
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCreateEntity = () => {
    if (!createForm.name.trim()) {
      dispatch(showError({
        title: 'Validation Error',
        message: 'Entity name is required',
        duration: 3000,
      }));
      return;
    }

    // TODO: Implement entity creation
    dispatch(showInfo({
      title: 'Coming Soon',
      message: 'Entity creation functionality will be implemented soon.',
      duration: 4000,
    }));
    setCreateDialogOpen(false);
    setCreateForm({ name: '', description: '' });
  };

  const getStatusColor = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return 'success';
      case EntityStatus.PROCESSING:
        return 'warning';
      case EntityStatus.INACTIVE:
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredEntities = entities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entity.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'file_count',
      headerName: 'Files',
      width: 100,
      type: 'number',
    },
    {
      field: 'url_count',
      headerName: 'URLs',
      width: 100,
      type: 'number',
    },
    {
      field: 'is_processed',
      headerName: 'Processed',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            onClick={() => navigate(`/entities/${params.row.id}`)}
            startIcon={<ViewIcon />}
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Knowledge Bases
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Knowledge Base
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search knowledge bases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={filteredEntities}
            columns={columns}
            loading={isLoading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Create Entity Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Knowledge Base</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateEntity} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Entities;
